import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import ForumMessageCard from "./ForumMessageCard";
import ScrollToTopOnMount from "../../../../ScrollToTop";
import BackButton from "components/common/BackButton";
import TicketAddMessage from "components/Forums/TicketAddMessage";
import Loading from "../../../Loading";
import Pagination from "components/common/Layout/Pagination";
import { ITEMS_PER_PAGE } from "constants/settings";
import { FORUM } from "endpointsConst";
import { showSuccessMessage, showErrorMessage } from "helpers/notifications";
import { ForumTicketData, ForumReplyData, ForumEditData, ForumMessage } from "types/forum";

import "./forumTicket.scss";

const ForumTicket: React.FC = () => {
    const authenticated = useSelector((state: any) => state.auth.authenticated);

    const { ticketId, topicId } = useParams<{ ticketId: string; topicId: string }>();
    const location = useLocation();
    const navigate = useNavigate();

    const [open, setOpen] = useState<boolean>(false);
    const [data, setData] = useState<ForumTicketData>({
        items: [],
        count: 0,
        theme: "",
        category_id: 0,
        category_title: "",
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [reply, setMessageToReply] = useState<ForumReplyData | "">("");
    const [editingMessage, setEditingMessage] = useState<ForumEditData | null>(null);
    const replyFormRef = useRef<HTMLDivElement>(null);

    const { t } = useTranslation();

    const loadMessages = useCallback((explicitOffset?: number) => {
        setLoading(true);
        const searchParams = explicitOffset !== undefined
            ? `?offset=${explicitOffset}`
            : location.search;
        axios
            .get<ForumTicketData>(FORUM + "/tickets/getById" + searchParams, {
                params: {
                    forum_ticket_id: ticketId,
                    limit: ITEMS_PER_PAGE,
                },
            })
            .then((res) => {
                setData(res.data);
            })
            .catch((e) => {
                console.log(e.response);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [location.search, ticketId]);

    const setReply = useCallback((replyData: ForumReplyData) => {
        setEditingMessage(null);
        setMessageToReply(replyData);
        setOpen(true);
    }, []);

    const handleStartEdit = useCallback((editData: ForumEditData) => {
        setMessageToReply("");
        setEditingMessage(editData);
        setOpen(true);
    }, []);

    useEffect(() => {
        if (open && replyFormRef.current) {
            replyFormRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [open, reply, editingMessage]);

    const moveOnLastPage = useCallback((messageAdded = false) => {
        const count = messageAdded ? Number(data.count) + 1 : Number(data.count);
        const lastPageOffset = Math.max(0, (Math.ceil(count / ITEMS_PER_PAGE) - 1) * ITEMS_PER_PAGE);
        const nextOffsetSearch = `?offset=${lastPageOffset}`;

        if (location.search !== nextOffsetSearch) {
            navigate({
                ...location,
                search: nextOffsetSearch,
            });
        }

        if (messageAdded) {
            // Explicitly load messages with the new offset (bypasses stale closure)
            loadMessages(lastPageOffset);
        }
    }, [navigate, location, data, loadMessages]);

    const handleEditMessage = useCallback(async (messageId: number, newMessage: string) => {
        try {
            await axios.patch(`${FORUM}/tickets/reply/${messageId}`, {
                message: newMessage,
            });
            showSuccessMessage(t("forums.topic.message_updated"));
            loadMessages();
            setEditingMessage(null);
            setOpen(false);
        } catch (error) {
            console.error("Error editing message:", error);
            showErrorMessage(t("errors.serverError"));
            throw error;
        }
    }, [loadMessages, t]);

    const handleDeleteMessage = useCallback(async (messageId: number, closeCallback: () => void) => {
        try {
            await axios.delete(`${FORUM}/tickets/reply/${messageId}`);
            showSuccessMessage(t("forums.topic.message_deleted"));
            loadMessages();
            closeCallback();
        } catch (error) {
            console.error("Error deleting message:", error);
            showErrorMessage(t("errors.serverError"));
        }
    }, [loadMessages, t]);

    useEffect(() => {
        loadMessages();
    }, [loadMessages]);

    const hasPagination = data.count / ITEMS_PER_PAGE > 1;

    return (
        <>
            <ScrollToTopOnMount />
            <BackButton />
            <div className="forum-ticket">
                <h2 className="forum-ticket__ticket-title">{data.theme}</h2>
                {loading && <Loading />}
                <ul className="forum-ticket__list">
                    {data.items?.map((el: ForumMessage) => (
                        <ForumMessageCard
                            {...el}
                            setReply={setReply}
                            onEdit={handleStartEdit}
                            onDelete={handleDeleteMessage}
                            key={el.id}
                        />
                    ))}
                </ul>
            </div>
            {hasPagination && (
                <Pagination
                    itemsOnPage={ITEMS_PER_PAGE}
                    count={data.count}
                    url={`/forums/topic/${topicId}/ticket/${ticketId}`}
                />
            )}
            {authenticated &&
                (!open ? (
                    <button
                        className="forum-ticket__add-button button--blue"
                        onClick={() => setOpen(!open)}
                        type="button"
                    >
                        {t("forums.topic.add_button")}
                    </button>
                ) : (
                    <div ref={replyFormRef}>
                        <TicketAddMessage
                            setOpen={setOpen}
                            id={ticketId}
                            loadMessages={loadMessages}
                            reply={reply}
                            setReply={setReply}
                            onSend={moveOnLastPage}
                            editingMessage={editingMessage}
                            onEditMessage={handleEditMessage}
                            setEditingMessage={setEditingMessage}
                        />
                    </div>
                ))}
        </>
    );
};

export default ForumTicket;
